import I18n from 'i18n-js';
import React from 'react';
import classNames from 'classnames';
import bind from '@reverbdotcom/commons/src/bind';

import Suggestion, { CompletionOption } from './suggestion';
import lowlight from './lowlight';

import {
  reverb_search_Money as Money,
  reverb_search_Inventory as Inventory,
} from '@reverbdotcom/commons/src/gql/graphql';

import { PriceDisplay } from '@reverbdotcom/commons/src/components/PriceDisplay';
import { formatAmountCents } from '@reverbdotcom/commons/src/money';

interface IProps {
  suggestion: Suggestion;
  active: boolean;
  lastTypedValue: string;
  selectSuggestion: (suggestion: Suggestion) => void;
}


export default class CspCompletionCard extends React.Component<IProps, null> {

  get option(): CompletionOption {
    return this.props.suggestion.completion;
  }

  get inventoryNew(): Inventory {
    return this.option.completionPayload.inventoryNew;
  }

  get inventoryUsed(): Inventory {
    return this.option.completionPayload.inventoryUsed;
  }

  get listingTotal(): number {
    return this.inventoryUsed.listingCount + this.inventoryNew.listingCount;
  }

  get minPriceNew(): Money {
    return this.option.completionPayload.inventoryNew.listingMinPrice;
  }

  get minPriceUsed(): Money {
    return this.option.completionPayload.inventoryUsed.listingMinPrice;
  }

  get hasNoListings(): boolean {
    return this.listingTotal === 0;
  }

  get hasOnlyNewListings(): boolean {
    return !this.inventoryUsed.listingCount;
  }

  get hasOnlyUsedListings(): boolean {
    return !this.inventoryNew.listingCount;
  }

  get minPrice(): Money {
    if (this.hasNoListings) return null;
    if (this.hasOnlyNewListings) return this.minPriceNew;
    if (this.hasOnlyUsedListings) return this.minPriceUsed;

    if (this.minPriceUsed.cents < this.minPriceNew.cents) return this.minPriceUsed;

    return this.minPriceNew;
  }

  get minPriceDisplay(): string {
    const money = { amountCents: this.minPrice.cents, currency: this.minPrice.currency };

    return formatAmountCents(money);
  }

  @bind
  submitSelection(e): void {
    e.preventDefault();

    this.props.selectSuggestion(this.props.suggestion);
  }

  renderTitle() {
    const title = lowlight({
      input: this.props.lastTypedValue,
      output: this.option.output,
      emphasize: (idx, em) => <React.Fragment key={idx}>{em.pre}<b>{em.word}</b>{em.post} </React.Fragment>,
      noop: (idx, em) => <React.Fragment key={idx}>{em.word} </React.Fragment>,
    });

    return (
      <div className="csp__suggestion__title">
        {title}
      </div>
    );
  }

  renderImage() {
    const image = this.option.completionPayload.thumbnailUrl;

    return (
      <div className="csp__suggestion__image">
        <div className="csp__suggestion__image__inner">
          <img src={image} alt={this.option.output} />
        </div>
      </div>
    );
  }

  renderPrices() {
    const { minPrice } = this;
    if (!minPrice || !minPrice.cents) return null;

    return (
      <div className="csp__suggestion__prices">
        {I18n.t('discovery.siteSearch.cspCompletionCard.availableFrom', { count: this.listingTotal })}
        <span className="csp__suggestion__prices__price">
          <PriceDisplay
            amountCents={minPrice.cents}
            display={this.minPriceDisplay}
          />
        </span>
      </div>
    );
  }

  render() {
    if (!this.option) return null;
    if (this.hasNoListings) return null;

    const classes = classNames(
      'site-search__dropdown__section__item',
      'site-search__dropdown__section__item--csp',
      { 'site-search__dropdown__section__item--selected': this.props.active },
    );

    return (
      <div key={this.option.id} className={classes}>
        {/* disabling inline here so we can enable this rule globally, but this should be fixed the next time this file is touched */}
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a onClick={this.submitSelection}>
          <div className="csp__suggestion">
            {this.renderImage()}
            <div className="csp__suggestion__content">
              {this.renderTitle()}
              {this.renderPrices()}
            </div>
          </div>
        </a>
      </div>
    );
  }
}
